import { trackTemplateFormatSelect } from '@anm/analytic';
import isMobile from '@anm/helpers/is/isMobile';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useState, FC } from 'react';
import { useSelector } from 'react-redux';

import { templateSelectors } from '../../../store/modules/template';
import { userSelectors } from '../../../store/modules/user';
import TemplateEditModal from '../TemplateEditModal';
import TemplateVideoPlayer, { TemplateVideoPlayerWrapperHeights } from '../TemplateVideoPlayer';
import { VideoRatioDirections, VideoRatioItemProps } from '../VideoRatioList';

import MobileAlert from './MobileAlert';
import TemplateVideoBottom, { TemplateVideoBottomProps } from './TemplateVideoBottom';
import TemplateVideoWithControlsWrapper, {
  ActionsDropdownWrapper,
  TemplateVideoPlayerHorizontalWrapper
} from './Wrapper';
import { useActionDropdown, useVideoFormat } from './hooks';
import {
  TemplateVideoPlayerWithRatioListMap,
  TemplateVideoPlayerWrapperHeightsMap,
  TemplateVideoWithControlsOrientations
} from './types';

export { getTemplateId } from './helpers';

export type TemplateVideoWithControlsProps = TemplateVideoBottomProps & {
  direction: VideoRatioDirections;
  orientation: TemplateVideoWithControlsOrientations;
  heightDependence: TemplateVideoPlayerWrapperHeights;
  activeRatioItemColor?: string;
  onTemplateEdit(): void;
};

const TemplateVideoWithControls: FC<TemplateVideoWithControlsProps> = props => {
  const [isEditModalOpen, openEditModal, closeEditModal] = useSwitchState();
  const [isMobileAlertOpen, openMobileAlert, closeMobileAlert] = useSwitchState();
  const [currentRatioFormat, setCurrentRatioFormat] = useState<VideoRatioItemProps>();
  const currentTemplate = useSelector(templateSelectors.selectCurrentTemplate);
  const isUserStaff = useSelector(userSelectors.selectIsStaffUser);

  const { variant, direction, orientation, activeRatioItemColor } = props;

  const handleChangeRatio = useCallback(
    (formatName: VideoRatioItemProps) => {
      if (!currentTemplate) return;
      const { templateId, displayName } = currentTemplate;

      setCurrentRatioFormat(formatName);
      trackTemplateFormatSelect(displayName, templateId, formatName.name);
    },
    [currentTemplate]
  );
  const isVideoVertical = useVideoFormat();
  const actions = useActionDropdown(openEditModal, currentRatioFormat);

  return (
    <>
      {isMobileAlertOpen && (
        <MobileAlert closeIconColor="black" dialogBackground="#BDDAFA" onModalClose={closeMobileAlert} />
      )}
      {currentTemplate && isEditModalOpen && <TemplateEditModal onModalClose={closeEditModal} />}
      <TemplateVideoWithControlsWrapper {...props}>
        <TemplateVideoPlayerHorizontalWrapper {...{ isVideoVertical }}>
          {isUserStaff && !isMobile() && <ActionsDropdownWrapper {...{ actions }} />}
          <TemplateVideoPlayer
            onTemplateEdit={props.onTemplateEdit}
            onChangeRatio={handleChangeRatio}
            currentRatioItem={currentRatioFormat}
            templateKind={currentTemplate?.meta.kind}
            {...{ variant, direction, activeRatioItemColor }}
            position={TemplateVideoPlayerWithRatioListMap[orientation]}
            heightDependence={TemplateVideoPlayerWrapperHeightsMap[orientation]}
          />
        </TemplateVideoPlayerHorizontalWrapper>
        <TemplateVideoBottom {...{ currentRatioFormat, ...props }} onMobileEdit={openMobileAlert} />
      </TemplateVideoWithControlsWrapper>
    </>
  );
};

export default TemplateVideoWithControls;
